import { c as _c } from "react/compiler-runtime";
import { usePotentialChangeOrdersControllerFindAll } from "@dc/gateway-jsonapi";
import { Badge, Chip, SparklesIcon, WorkflowsIcon } from "@dc/ui";
import { useTranslations } from "next-intl";
import { useMemo } from "react";
import { ArchiveLinear, BellLinear, BuildingsLinear, ChartLinear, ChecklistMinimalisticLinear, ClipboardCheckLinear, PenNewSquareLinear, ShareCircleLinear } from "solar-icon-set";
import { useChangeOrderAccess } from "@/hooks/useChangeOrderAccess";
import { useNavigationUpdatesEnabled } from "@/hooks/useNavigationUpdatesEnabled";
import { useNotifications } from "@/hooks/useNotifications";
import { useOrganization } from "@/hooks/useOrganization";
import { useProjectsHomeHref } from "@/hooks/useProjectsHomeHref";
import { useTeamsContext } from "@/hooks/useTeamsContext";
import { useTeamTaskNavAccess } from "@/hooks/useTeamTaskNavAccess";
import { useResumeTrialOnboarding } from "@/hooks/useTrialUploadOnboarding/useResumeTrialOnboarding";
import { getRelationshipScopeId } from "@/lib/getRelationshipScopeId";
import type { SidebarItem } from "../Sidebar.types";
export interface NavGroup {
  label: string;
  items: SidebarItem[];
  requirePermission?: {
    action: string;
    resource: string;
    scopeId?: string;
  };
}
export const useNavigationItems = () => {
  const $ = _c(137);
  const t = useTranslations("navigation");
  const {
    href: filesHref
  } = useProjectsHomeHref();
  const {
    href: trialOnboardingHref,
    isLoading: isTrialOnboardingLoading
  } = useResumeTrialOnboarding();
  const {
    currentScopeId
  } = useTeamsContext();
  const canSeeTeamTasks = useTeamTaskNavAccess(currentScopeId);
  const {
    hasAccess: hasChangeOrderAccess,
    isError: isChangeOrderAccessError,
    isLoading: isChangeOrderAccessLoading
  } = useChangeOrderAccess();
  const showChanges = hasChangeOrderAccess && !isChangeOrderAccessLoading && !isChangeOrderAccessError;
  const {
    currentOrganization
  } = useOrganization();
  const t0 = currentOrganization?.relationships?.scope?.data;
  let t1;
  if ($[0] !== t0) {
    t1 = getRelationshipScopeId(t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const organizationScopeId = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      number: 1,
      size: 1
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== organizationScopeId) {
    t3 = {
      page: t2,
      filter: {
        status: "DETECTED",
        "scope.parent.parent.parentId": organizationScopeId
      }
    };
    $[3] = organizationScopeId;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = showChanges && Boolean(organizationScopeId);
  let t5;
  if ($[5] !== t4) {
    t5 = {
      query: {
        enabled: t4,
        staleTime: 30000
      }
    };
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const {
    data: detectedChanges
  } = usePotentialChangeOrdersControllerFindAll(t3, t5);
  const detectedChangesCount = detectedChanges?.meta?.total;
  const navigationUpdatesEnabled = useNavigationUpdatesEnabled();
  const {
    unreadCount,
    unreadCountLabel,
    toggleDrawer
  } = useNotifications();
  let t6;
  if ($[7] !== t) {
    t6 = t("groups.overview");
    $[7] = t;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const groupOverview = t6;
  let t7;
  if ($[9] !== t) {
    t7 = t("groups.tools");
    $[9] = t;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const groupTools = t7;
  let t8;
  if ($[11] !== t) {
    t8 = t("groups.changes");
    $[11] = t;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const groupChanges = t8;
  let t9;
  if ($[13] !== t) {
    t9 = t("items.files");
    $[13] = t;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  const itemFiles = t9;
  let t10;
  if ($[15] !== t) {
    t10 = t("items.usage_dashboard");
    $[15] = t;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  const itemUsageDashboard = t10;
  let t11;
  if ($[17] !== t) {
    t11 = t("items.chat");
    $[17] = t;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const itemChat = t11;
  let t12;
  if ($[19] !== t) {
    t12 = t("items.workflows");
    $[19] = t;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const itemWorkflows = t12;
  let t13;
  if ($[21] !== t) {
    t13 = t("items.tasks");
    $[21] = t;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  const itemTasks = t13;
  let t14;
  if ($[23] !== t) {
    t14 = t("items.assigned_to_me");
    $[23] = t;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  const itemAssignedToMe = t14;
  let t15;
  if ($[25] !== t) {
    t15 = t("items.shared_with_me");
    $[25] = t;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  const itemSharedWithMe = t15;
  let t16;
  if ($[27] !== t) {
    t16 = t("items.archive");
    $[27] = t;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  const itemArchive = t16;
  let t17;
  if ($[29] !== t) {
    t17 = t("items.changes");
    $[29] = t;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  const itemChanges = t17;
  let t18;
  if ($[31] !== t) {
    t18 = t("badges.new");
    $[31] = t;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  const badgeNew = t18;
  let t19;
  if ($[33] !== t) {
    t19 = t("groups.workspace");
    $[33] = t;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  const groupWorkspace = t19;
  let t20;
  if ($[35] !== t) {
    t20 = t("items.change_management");
    $[35] = t;
    $[36] = t20;
  } else {
    t20 = $[36];
  }
  const itemChangeManagement = t20;
  let t21;
  if ($[37] !== t) {
    t21 = t("actions.notifications");
    $[37] = t;
    $[38] = t21;
  } else {
    t21 = $[38];
  }
  const itemNotifications = t21;
  let t22;
  bb0: {
    let t23;
    if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
      t23 = <BuildingsLinear size={20} />;
      $[39] = t23;
    } else {
      t23 = $[39];
    }
    let t24;
    if ($[40] !== filesHref || $[41] !== itemFiles) {
      t24 = {
        key: "files",
        href: filesHref,
        icon: t23,
        title: itemFiles
      };
      $[40] = filesHref;
      $[41] = itemFiles;
      $[42] = t24;
    } else {
      t24 = $[42];
    }
    const filesItem = t24;
    let t25;
    if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
      t25 = <ArchiveLinear size={20} />;
      $[43] = t25;
    } else {
      t25 = $[43];
    }
    let t26;
    if ($[44] !== itemArchive) {
      t26 = {
        key: "archive",
        href: "/archive",
        icon: t25,
        title: itemArchive
      };
      $[44] = itemArchive;
      $[45] = t26;
    } else {
      t26 = $[45];
    }
    const archiveItem = t26;
    let t27;
    if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
      t27 = <ChartLinear size={20} />;
      $[46] = t27;
    } else {
      t27 = $[46];
    }
    let t28;
    if ($[47] !== badgeNew) {
      t28 = <Chip color="pop" variant="solid" className="h-5 p-0! text-sm leading-none" size="sm">{badgeNew}</Chip>;
      $[47] = badgeNew;
      $[48] = t28;
    } else {
      t28 = $[48];
    }
    let t29;
    if ($[49] !== itemUsageDashboard || $[50] !== t28) {
      t29 = {
        key: "usage-dashboard",
        href: "/usage-dashboard",
        icon: t27,
        title: itemUsageDashboard,
        featureFlag: "usage-dashboard",
        endContent: t28
      };
      $[49] = itemUsageDashboard;
      $[50] = t28;
      $[51] = t29;
    } else {
      t29 = $[51];
    }
    const usageDashboardItem = t29;
    const t30 = trialOnboardingHref ?? "/chat";
    let t31;
    if ($[52] === Symbol.for("react.memo_cache_sentinel")) {
      t31 = <SparklesIcon className="size-5" />;
      $[52] = t31;
    } else {
      t31 = $[52];
    }
    let t32;
    if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
      t32 = {
        action: "read",
        resource: "THREAD"
      };
      $[53] = t32;
    } else {
      t32 = $[53];
    }
    let t33;
    if ($[54] !== isTrialOnboardingLoading || $[55] !== itemChat || $[56] !== t30) {
      t33 = {
        key: "chat",
        href: t30,
        isDisabled: isTrialOnboardingLoading,
        icon: t31,
        title: itemChat,
        requirePermission: t32
      };
      $[54] = isTrialOnboardingLoading;
      $[55] = itemChat;
      $[56] = t30;
      $[57] = t33;
    } else {
      t33 = $[57];
    }
    const chatItem = t33;
    let t34;
    if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
      t34 = <WorkflowsIcon className="size-5" />;
      $[58] = t34;
    } else {
      t34 = $[58];
    }
    let t35;
    if ($[59] !== itemWorkflows) {
      t35 = {
        key: "workflows",
        href: "/workflows",
        icon: t34,
        title: itemWorkflows
      };
      $[59] = itemWorkflows;
      $[60] = t35;
    } else {
      t35 = $[60];
    }
    const workflowsItem = t35;
    let t36;
    if ($[61] !== canSeeTeamTasks || $[62] !== itemTasks) {
      t36 = canSeeTeamTasks ? [{
        key: "tasks",
        href: "/tasks",
        icon: <ClipboardCheckLinear size={20} />,
        title: itemTasks
      }] : [];
      $[61] = canSeeTeamTasks;
      $[62] = itemTasks;
      $[63] = t36;
    } else {
      t36 = $[63];
    }
    const tasksItems = t36;
    let t37;
    if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
      t37 = <ChecklistMinimalisticLinear size={20} />;
      $[64] = t37;
    } else {
      t37 = $[64];
    }
    let t38;
    if ($[65] !== itemAssignedToMe) {
      t38 = {
        key: "assigned-to-me",
        href: "/assigned-to-me",
        icon: t37,
        title: itemAssignedToMe,
        featureFlag: "sidebar-show-assigned-to-me-ff-73"
      };
      $[65] = itemAssignedToMe;
      $[66] = t38;
    } else {
      t38 = $[66];
    }
    const assignedToMeItem = t38;
    let t39;
    if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
      t39 = <ShareCircleLinear size={20} />;
      $[67] = t39;
    } else {
      t39 = $[67];
    }
    let t40;
    if ($[68] !== itemSharedWithMe) {
      t40 = {
        key: "shared-with-me",
        href: "/shared-with-me",
        icon: t39,
        title: itemSharedWithMe,
        featureFlag: "sidebar-show-shared-with-me-ff-73"
      };
      $[68] = itemSharedWithMe;
      $[69] = t40;
    } else {
      t40 = $[69];
    }
    const sharedWithMeItem = t40;
    let t41;
    if ($[70] !== detectedChangesCount) {
      t41 = detectedChangesCount ? <Chip color="pop" size="sm" variant="solid" className="min-h-6 min-w-6 flex-none">{detectedChangesCount}</Chip> : null;
      $[70] = detectedChangesCount;
      $[71] = t41;
    } else {
      t41 = $[71];
    }
    const changesEndContent = t41;
    let t42;
    if ($[72] !== changesEndContent || $[73] !== showChanges) {
      t42 = title => showChanges ? [{
        key: "changes",
        href: "/changes",
        icon: <PenNewSquareLinear size={20} />,
        title,
        endContent: changesEndContent
      }] : [];
      $[72] = changesEndContent;
      $[73] = showChanges;
      $[74] = t42;
    } else {
      t42 = $[74];
    }
    const changesItems = t42;
    if (!navigationUpdatesEnabled) {
      let t43;
      if ($[75] !== archiveItem || $[76] !== filesItem) {
        t43 = [filesItem, archiveItem];
        $[75] = archiveItem;
        $[76] = filesItem;
        $[77] = t43;
      } else {
        t43 = $[77];
      }
      let t44;
      if ($[78] !== groupOverview || $[79] !== t43) {
        t44 = {
          label: groupOverview,
          items: t43
        };
        $[78] = groupOverview;
        $[79] = t43;
        $[80] = t44;
      } else {
        t44 = $[80];
      }
      let t45;
      if ($[81] !== assignedToMeItem || $[82] !== chatItem || $[83] !== sharedWithMeItem || $[84] !== tasksItems || $[85] !== usageDashboardItem || $[86] !== workflowsItem) {
        t45 = [chatItem, workflowsItem, ...tasksItems, assignedToMeItem, sharedWithMeItem, usageDashboardItem];
        $[81] = assignedToMeItem;
        $[82] = chatItem;
        $[83] = sharedWithMeItem;
        $[84] = tasksItems;
        $[85] = usageDashboardItem;
        $[86] = workflowsItem;
        $[87] = t45;
      } else {
        t45 = $[87];
      }
      let t46;
      if ($[88] !== groupTools || $[89] !== t45) {
        t46 = {
          label: groupTools,
          items: t45
        };
        $[88] = groupTools;
        $[89] = t45;
        $[90] = t46;
      } else {
        t46 = $[90];
      }
      let t47;
      if ($[91] !== changesItems || $[92] !== groupChanges || $[93] !== itemChanges || $[94] !== showChanges) {
        t47 = showChanges ? [{
          label: groupChanges,
          items: changesItems(itemChanges)
        }] : [];
        $[91] = changesItems;
        $[92] = groupChanges;
        $[93] = itemChanges;
        $[94] = showChanges;
        $[95] = t47;
      } else {
        t47 = $[95];
      }
      let t48;
      if ($[96] !== t44 || $[97] !== t46 || $[98] !== t47) {
        t48 = [t44, t46, ...t47];
        $[96] = t44;
        $[97] = t46;
        $[98] = t47;
        $[99] = t48;
      } else {
        t48 = $[99];
      }
      t22 = t48;
      break bb0;
    }
    const t43 = unreadCount === 0;
    let t44;
    if ($[100] === Symbol.for("react.memo_cache_sentinel")) {
      t44 = <BellLinear size={20} />;
      $[100] = t44;
    } else {
      t44 = $[100];
    }
    let t45;
    if ($[101] !== t43 || $[102] !== unreadCountLabel) {
      t45 = <Badge content={unreadCountLabel} color="default" size="sm" shape="circle" className="flex-none border-0 bg-bg-interactive-pop-solid-default text-text-body-onPop!" isInvisible={t43}>{t44}</Badge>;
      $[101] = t43;
      $[102] = unreadCountLabel;
      $[103] = t45;
    } else {
      t45 = $[103];
    }
    let t46;
    if ($[104] !== itemNotifications || $[105] !== t45 || $[106] !== toggleDrawer) {
      t46 = {
        key: "notifications",
        icon: t45,
        title: itemNotifications,
        onPress: toggleDrawer
      };
      $[104] = itemNotifications;
      $[105] = t45;
      $[106] = toggleDrawer;
      $[107] = t46;
    } else {
      t46 = $[107];
    }
    const notificationsItem = t46;
    let t47;
    if ($[108] !== changesItems || $[109] !== itemChangeManagement) {
      t47 = changesItems(itemChangeManagement);
      $[108] = changesItems;
      $[109] = itemChangeManagement;
      $[110] = t47;
    } else {
      t47 = $[110];
    }
    let t48;
    if ($[111] !== archiveItem || $[112] !== chatItem || $[113] !== filesItem || $[114] !== t47) {
      t48 = [filesItem, chatItem, ...t47, archiveItem];
      $[111] = archiveItem;
      $[112] = chatItem;
      $[113] = filesItem;
      $[114] = t47;
      $[115] = t48;
    } else {
      t48 = $[115];
    }
    let t49;
    if ($[116] !== groupWorkspace || $[117] !== t48) {
      t49 = {
        label: groupWorkspace,
        items: t48
      };
      $[116] = groupWorkspace;
      $[117] = t48;
      $[118] = t49;
    } else {
      t49 = $[118];
    }
    let t50;
    if ($[119] !== assignedToMeItem || $[120] !== notificationsItem || $[121] !== sharedWithMeItem || $[122] !== tasksItems || $[123] !== usageDashboardItem || $[124] !== workflowsItem) {
      t50 = [notificationsItem, workflowsItem, ...tasksItems, assignedToMeItem, sharedWithMeItem, usageDashboardItem];
      $[119] = assignedToMeItem;
      $[120] = notificationsItem;
      $[121] = sharedWithMeItem;
      $[122] = tasksItems;
      $[123] = usageDashboardItem;
      $[124] = workflowsItem;
      $[125] = t50;
    } else {
      t50 = $[125];
    }
    let t51;
    if ($[126] !== groupTools || $[127] !== t50) {
      t51 = {
        label: groupTools,
        items: t50
      };
      $[126] = groupTools;
      $[127] = t50;
      $[128] = t51;
    } else {
      t51 = $[128];
    }
    let t52;
    if ($[129] !== t49 || $[130] !== t51) {
      t52 = [t49, t51];
      $[129] = t49;
      $[130] = t51;
      $[131] = t52;
    } else {
      t52 = $[131];
    }
    t22 = t52;
  }
  const navigationGroups = t22;
  let t23;
  if ($[132] !== navigationGroups) {
    t23 = navigationGroups.flatMap(_temp);
    $[132] = navigationGroups;
    $[133] = t23;
  } else {
    t23 = $[133];
  }
  const defaultNavItems = t23;
  let t24;
  if ($[134] !== defaultNavItems || $[135] !== navigationGroups) {
    t24 = {
      navigationGroups,
      defaultNavItems
    };
    $[134] = defaultNavItems;
    $[135] = navigationGroups;
    $[136] = t24;
  } else {
    t24 = $[136];
  }
  return t24;
};
function _temp(group) {
  return group.items;
}